/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {switchMap, tap} from 'rxjs/operators';

import {Destroyable} from '../../destroyable.component';
import {CliCorePropertiesService} from '../../navigation/cli-core-properties.service';
import {UserService} from '../user';
import {Files} from '../utils/files';

const ALL_LANGUAGES: { [key: string]: string } = {
  'de-DE': 'Deutsch (Germany)',
  'ar-EG': '(Egypt) مصر',
  'en-GB': 'English (UK)',
  'en-US': 'English (US)',
  'en-AU': 'English (Australia)',
  'fr-FR': 'Français (France)',
  'fr-LU': 'Français (Luxembourg)',
  'it-IT': 'italiano (Italy)',
  'es-ES': 'Español (Spain)',
  'zh-CN': '中文 (China)',
  'nl-NL': 'Nederlands(Netherlands)',
  'pt-PT': 'Português (Portugal)',
  'ru-RU': 'Русский (Russia)'
};

@Injectable()
export class I18nManager extends Destroyable {

  public static DEFAULT_LANGUAGE = 'en-US';
  static LOCAL_STORAGE_LOCALE_KEY_NAME = 'gp-lang';

  private static localTranslationsByLanguage: { [key: string]: { [key: string]: string } } = null;

  private availableLanguageCodes: string[] = I18nManager.allLanguageCodes;

  public static getLocalTranslations(language: string): { [key: string]: string } {
    if (!I18nManager.localTranslationsByLanguage) {
      I18nManager.loadLocalTranslations();
    }
    return I18nManager.localTranslationsByLanguage[language];
  }

  private static loadLocalTranslations() {
    const localTranslations: Array<{ [key: string]: string }> = this.localTranslations();
    this.localTranslationsByLanguage = {};
    for (const language of this.allLanguageCodes) {
      this.localTranslationsByLanguage[language] = this.extractTranslations(localTranslations, language);
    }
  }

  private static extractTranslations(translations: Array<{ [key: string]: string }>, language: string): {
    [key: string]: string
  } {
    const translationsOfLanguage = {};
    translations.forEach(translationItem => {
      translationsOfLanguage[translationItem.key] = translationItem[language] || translationItem[I18nManager.DEFAULT_LANGUAGE];
    });
    return translationsOfLanguage;
  }

  constructor(private translateService: TranslateService,
              private userService: UserService,
              private cliCorePropertiesService: CliCorePropertiesService) {
    super();
    this.initialize();
  }

  get availableLanguages(): { [key: string]: string } {
    return Object.keys(ALL_LANGUAGES)
      .filter(language => this.availableLanguageCodes.includes(language))
      .reduce((language, key) => {
        language[key] = ALL_LANGUAGES[key];
        return language;
      }, {});
  }

  get currentLanguage(): string {
    return this.translateService.currentLang;
  }

  static get allLanguageCodes(): string[] {
    return Object.keys(ALL_LANGUAGES);
  }

  private static localTranslations(): Array<{ [key: string]: string }> {
    return require('./translations.json');
  }

  /**
   * 1/
   *  Add all languages as available languages
   *  Set default language en-US
   *  Use user language from localStorage if available or default language en-US
   * 2/
   *  Load cli core properties
   *  If languages are defined in cli core properties, store them for later filtering
   * 3/
   *  Get user language and use it if possible
   */
  private initialize() {
    this.translateService.addLangs(I18nManager.allLanguageCodes);
    this.translateService.setDefaultLang(I18nManager.DEFAULT_LANGUAGE);
    this.translateService.use(localStorage.getItem(I18nManager.LOCAL_STORAGE_LOCALE_KEY_NAME) || I18nManager.DEFAULT_LANGUAGE);

    this.cliCorePropertiesService.get()
      .pipe(
        tap(properties => {
          if (properties?.languages?.length) {
            this.availableLanguageCodes = properties.languages;
          }
        }),
        switchMap(() => this.userService.getUser()),
        tap(user => this.setLanguage(user?.language))
      )
      .subscribe();
  }

  public exportTranslationsToCSV() {
    // CSV header: key,en-US,fr-FR ...
    const columns = [
      {name: 'key', field: 'key'},
      ...I18nManager.allLanguageCodes.map(language => {
        return {name: language, field: language};
      })
    ];

    Files.exportToCSV(
      I18nManager.localTranslations(),
      columns,
      'translations',
      this.userService
    );
  }

  private setLanguage(language: string): void {
    if (!language || !this.availableLanguageCodes.includes(language)) {
      language = I18nManager.DEFAULT_LANGUAGE;
    }
    this.translateService.use(language);
    localStorage.setItem(I18nManager.LOCAL_STORAGE_LOCALE_KEY_NAME, language);
  }
}
